// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react'
import { Loader2, Boxes, Cog, PlayCircle, History, Sparkles, Printer, CheckCircle2, AlertTriangle, Save } from 'lucide-react'
import * as api from '../../api/client'
import { friendlyError } from '../../api/errors'
import SearchableSelect from '../../components/common/SearchableSelect'
import { useTableFunnels, type FunnelColumn } from '../../components/common/TableFunnels'

// Pure formatters, at module level so the funnel columns below can print what the cells print.
const money = (n: number | null | undefined) => n == null ? '—' : `$${n.toFixed(2)}`
const unitLabel = (i: { unit_price?: number | null; unit_of_issue?: string | null }) =>
  i.unit_price == null ? '—' : i.unit_of_issue && i.unit_of_issue !== 'each' ? `$${i.unit_price.toFixed(2)} / ${i.unit_of_issue}` : `$${i.unit_price.toFixed(2)}`

// D-UI-2: a funnel on every column of the four headed tables. Values are what the cell shows.
type AiFieldRow = { field: string; value: unknown; cites: NonNullable<api.SupplyAiLookup['citations']> }
const aiValueText = (value: unknown) => (typeof value === 'object' ? JSON.stringify(value) : String(value))
const HISTORY_FUNNELS: FunnelColumn<api.PickListHistoryRow>[] = [
  { key: 'label', label: 'Label', value: h => h.label ?? h.drug_name ?? '—' },
  { key: 'state', label: 'State', value: h => h.state ?? '—' },
  { key: 'cost', label: 'Cost', value: h => h.total_cost },
  { key: 'generated', label: 'Generated', value: h => h.generated_at },
  { key: 'reused', label: 'Reused', value: h => (h.reuse_count > 0 ? `${h.reuse_count}×` : '—') },
]
const AI_FUNNELS: FunnelColumn<AiFieldRow>[] = [
  { key: 'field', label: 'Field', value: r => r.field },
  { key: 'value', label: 'Proposed value', value: r => aiValueText(r.value) },
  { key: 'reference', label: 'Reference', kind: 'text',
    value: r => (r.cites.length === 0 ? 'no citation — will not save' : r.cites.map(c => c.title ?? c.url).join('\n')) },
]
const PICK_FUNNELS: FunnelColumn<api.PickListItem>[] = [
  { key: 'category', label: 'Category', value: i => (i.category ?? '').replace(/_/g, ' ') },
  { key: 'item', label: 'Item', value: i => i.name },
  { key: 'vendor', label: 'Vendor #', value: i => i.vendor_item_number || '—' },
  { key: 'qty', label: 'Qty', value: i => i.generated_qty },
  { key: 'unit', label: 'Unit', value: i => unitLabel(i) },
  { key: 'ext', label: 'Ext', value: i => i.ext_price },
  { key: 'rule', label: 'Rule', kind: 'text', value: i => i.rule_note ?? i.rule_code },
]
const PUMP_FUNNELS: FunnelColumn<api.SupplyPumpSet>[] = [
  { key: 'name', label: 'Name', value: p => p.name },
  { key: 'pump', label: 'Pump', value: p => p.pump },
  { key: 'filter', label: 'Filter micron', value: p => p.filter_micron ?? '—' },
  { key: 'priming', label: 'Priming (mL)', value: p => p.priming_volume_ml ?? '—' },
  { key: 'vendor', label: 'Vendor #', value: p => p.vendor_item_number ?? '—' },
]

/**
 * D-CRX-46 LB-4 (Set C), rebuilt per CareRx_Supply_Generator_Handoff.md. Supply catalog + the
 * rules-driven pick-list generator:
 *  - Drug dropdown lists EVERY discovered drug, searchable; unapproved drugs render disabled with
 *    the reason (generation is blocked server-side too — SUP-08).
 *  - Inputs per handoff §4: drug, access, site of care, pump (visit-level), pack level.
 *  - Result shows completeness state, the "required by profile, not in catalog" block, the
 *    excluded-by-rule panel, and prorated cost ("$2.30 / box").
 *  - History panel: reuse a saved list instead of regenerating (owner ask 2026-08-31).
 *  - AI supply lookup: allow-listed official sources only; each value shows its citation; a
 *    pharmacist accepts to save onto the drug profile (which is also what the Drug Reference
 *    record shows) — the "save to drug profile" option.
 */
export default function CareRxSupplyCatalog() {
  const [cat, setCat] = useState<api.SupplyCategory[]>([])
  const [items, setItems] = useState<api.SupplyItem[]>([])
  const [pumps, setPumps] = useState<api.SupplyPumpSet[]>([])
  const [drugs, setDrugs] = useState<api.SupplyDrug[]>([])
  const [rulesVersion, setRulesVersion] = useState('')
  const [loading, setLoading] = useState(true)
  const [msg, setMsg] = useState('')
  // generator inputs (§4)
  const [drug, setDrug] = useState('')
  const [access, setAccess] = useState('peripheral')
  const [site, setSite] = useState('home')
  const [pump, setPump] = useState('CURLIN 6000')
  const [pack, setPack] = useState('standard')
  const [gen, setGen] = useState<api.PickListResult | null>(null)
  const [busy, setBusy] = useState(false)
  // history
  const [history, setHistory] = useState<api.PickListHistoryRow[]>([])
  const [showHistory, setShowHistory] = useState(false)
  // AI lookup
  const [aiBusy, setAiBusy] = useState(false)
  const [ai, setAi] = useState<api.SupplyAiLookup | null>(null)
  const [aiMsg, setAiMsg] = useState('')
  const [acceptBusy, setAcceptBusy] = useState(false)

  const selectedDrug = useMemo(() => drugs.find(d => d.formulary_id === drug) ?? null, [drugs, drug])
  const isIv = (selectedDrug?.supply_route ?? 'IV') === 'IV'

  const load = useCallback(async () => {
    setLoading(true); setMsg('')
    try {
      const r = await api.getCareRxSupplyCatalog()
      setCat(r.categories); setItems(r.items); setPumps(r.pump_sets); setDrugs(r.drugs); setRulesVersion(r.rules_version)
      if (!r.drugs.some(d => d.formulary_id === drug)) {
        const first = r.drugs.find(d => d.approved) ?? r.drugs[0]
        if (first) setDrug(first.formulary_id)
      }
    } catch (e: any) { setMsg(friendlyError(e)) }
    finally { setLoading(false) }
  }, [drug])
  useEffect(() => { void load() }, [])   // eslint-disable-line react-hooks/exhaustive-deps

  const loadHistory = useCallback(async () => {
    try { setHistory((await api.listCareRxPickLists()).items) } catch { /* panel stays empty */ }
  }, [])
  useEffect(() => { if (showHistory) void loadHistory() }, [showHistory, loadHistory])

  const itemsByCat = useMemo(() => {
    const m: Record<string, api.SupplyItem[]> = {}
    for (const i of items) (m[i.category ?? 'uncat'] ??= []).push(i)
    return m
  }, [items])

  const pumpOptions = useMemo(() => {
    const names = Array.from(new Set(pumps.map(p => p.pump)))
    return names.map(n => ({
      value: n, label: n,
      // SUP-04: gravity disabled in the picker, with the reason, when the profile rules it out.
      disabled: n === 'Gravity' && selectedDrug?.gravity_permitted === 'no',
      hint: n === 'Gravity' && selectedDrug?.gravity_permitted === 'no' ? 'not permitted for this drug' : undefined,
    }))
  }, [pumps, selectedDrug])

  const generate = async () => {
    if (!drug) return
    setBusy(true); setMsg(''); setGen(null)
    try {
      setGen(await api.generateCareRxPickList({
        formulary_id: drug, access, site_of_care: site,
        pump: isIv ? pump : undefined, pack_level: pack,
      }))
      if (showHistory) void loadHistory()
    } catch (e: any) { setMsg(friendlyError(e)) } finally { setBusy(false) }
  }

  const reuse = async (id: string) => {
    setBusy(true); setMsg('')
    try {
      const saved = await api.reuseCareRxPickList(id)
      const h = saved.header
      setGen({
        id, formulary_id: h.formulary_id, drug_name: h.drug_name, label: h.label,
        pack_level: h.pack_level, site_of_care: h.site_of_care, access: h.access, pump: h.pump,
        pump_set: null, priming_volume_ml: null, state: h.state, rules_version: h.rules_version,
        items: saved.items.filter(i => !i.unmet), count: saved.items.filter(i => !i.unmet).length,
        total_cost: h.total_cost ?? 0, unmet: h.unmet ?? [], excluded: h.excluded ?? [],
        concentration_note: null, rule_errors: [],
        profile: {
          filter_min_micron: null, filter_max_micron: null, diluent: null, standard_final_volume_ml: null,
          final_conc_min_mg_ml: null, final_conc_max_mg_ml: null, co_infusion_allowed: h.co_infusion_allowed,
          premedication_required: null, stability_window: h.stability_window,
          stability_room_temp_hours: null, stability_refrigerated_hours: null,
          source_citation: h.source_citation, rescue_override_reason: h.rescue_override_reason,
        },
      })
      void loadHistory()
    } catch (e: any) { setMsg(friendlyError(e)) } finally { setBusy(false) }
  }

  const runAi = async () => {
    if (!drug) return
    setAiBusy(true); setAiMsg(''); setAi(null)
    try {
      // Reuse a prior successful lookup when one exists — nobody pays to ask twice.
      const past = await api.listCareRxSupplyAiLookups(drug)
      const prior = past.items.find(l => l.status === 'ok')
      if (prior) { setAi(prior); setAiMsg('showing the stored lookup — run again only if the label changed') }
      else setAi(await api.runCareRxSupplyAiLookup({ formulary_id: drug }))
    } catch (e: any) { setAiMsg(friendlyError(e)) } finally { setAiBusy(false) }
  }

  const rerunAi = async () => {
    if (!drug) return
    setAiBusy(true); setAiMsg('')
    try { setAi(await api.runCareRxSupplyAiLookup({ formulary_id: drug })) }
    catch (e: any) { setAiMsg(friendlyError(e)) } finally { setAiBusy(false) }
  }

  // "Save to drug profile": accept the proposal onto carerx_formulary — the same record the Drug
  // Reference details page reads, so the findings land in both places at once.
  const acceptAi = async () => {
    if (!ai?.id) return
    setAcceptBusy(true); setAiMsg('')
    try {
      const r = await api.acceptCareRxSupplyAiLookup(ai.id)
      setAiMsg(`saved to drug profile: ${r.accepted.length} fields, ${r.citations_saved} citations`)
      setAi({ ...ai, accepted_at: new Date().toISOString() })
      void load()
    } catch (e: any) { setAiMsg(friendlyError(e)) } finally { setAcceptBusy(false) }
  }

  // D-UI-2: the funnels sit above the early returns (hook rules); each takes the whole loaded set.
  const aiRows = useMemo<AiFieldRow[]>(() => Object.entries(ai?.fields ?? {}).map(([field, value]) => ({
    field, value, cites: (ai?.citations ?? []).filter(c => c.field === field),
  })), [ai])
  const historyFx = useTableFunnels(history, HISTORY_FUNNELS)
  const aiFx = useTableFunnels(aiRows, AI_FUNNELS)
  const pickFx = useTableFunnels(gen?.items, PICK_FUNNELS)
  const pumpFx = useTableFunnels(pumps, PUMP_FUNNELS)

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: muted.color, padding: 20 }}><Loader2 size={16} className="spin" /> Loading supply catalog…</div>
  if (msg && !cat.length) return <div style={{ fontSize: 13, color: '#b91c1c', padding: 12 }}>{msg}</div>

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={cardTitle}><Boxes size={16} /> Supply catalog</div>
          <p style={{ ...muted, marginTop: 4 }}>
            {cat.length} categories · {items.length} items · {pumps.length} pump sets · rules v{rulesVersion}.
            Config-as-data; pick lists generate by rules off an approved per-drug profile.
          </p>
        </div>
        <button style={ghostBtn} onClick={() => setShowHistory(s => !s)}><History size={14} /> History{history.length > 0 ? ` (${history.length})` : ''}</button>
      </div>

      {/* History tracker */}
      {showHistory && (
        <div style={card}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}><History size={15} /> Generated lists</div>
          {history.length === 0 && <div style={{ ...muted, fontSize: 12 }}>nothing generated yet</div>}
          {history.length > 0 && (
            <>{historyFx.chips}
            <table {...historyFx.tableProps} style={lineTable}>
              <thead><tr><th style={th}>Label {historyFx.funnel('label')}</th><th style={th}>State {historyFx.funnel('state')}</th><th style={th}>Cost {historyFx.funnel('cost')}</th><th style={th}>Generated {historyFx.funnel('generated')}</th><th style={th}>Reused {historyFx.funnel('reused')}</th><th style={th}></th></tr></thead>
              <tbody>
                {historyFx.rows.map(h => (
                  <tr key={h.id}>
                    <td style={td}>{h.label ?? h.drug_name ?? '—'}</td>
                    <td style={td}>{h.state === 'complete'
                      ? <span style={okBadge}><CheckCircle2 size={11} /> complete</span>
                      : <span style={warnBadge}><AlertTriangle size={11} /> {h.state ?? '—'}</span>}</td>
                    <td style={td}>{money(h.total_cost)}</td>
                    <td style={td}>{new Date(h.generated_at).toLocaleString()}</td>
                    <td style={td}>{h.reuse_count > 0 ? `${h.reuse_count}×` : '—'}</td>
                    <td style={td}>
                      <button style={btnSm} disabled={busy} onClick={() => void reuse(h.id)}>Open</button>{' '}
                      <button style={btnSm} onClick={() => { void api.patchCareRxPickList(h.id, { archived: true }).then(loadHistory) }}>Archive</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table></>
          )}
        </div>
      )}

      {/* Generator */}
      <div style={{ ...card, background: 'var(--color-surface-soft, #f7f9fa)' }}>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}><PlayCircle size={15} /> Generate a pick list</div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'end' }}>
          <label style={genLabel}>Drug — all discovered, type to search
            <SearchableSelect value={drug} onChange={v => { setDrug(v); setGen(null); setAi(null) }} ariaLabel="Drug"
              placeholder={drugs.length === 0 ? '— no drugs —' : 'Search drug…'} style={{ minWidth: 280 }}
              options={drugs.map(d => ({
                value: d.formulary_id,
                label: d.name,
                disabled: !d.approved,
                hint: d.approved ? undefined : 'profile not approved',
              }))} /></label>
          <label style={genLabel}>Access
            <SearchableSelect value={access} onChange={setAccess} ariaLabel="Access" style={{ minWidth: 130 }}
              options={['peripheral', 'port', 'picc', 'midline'].map(a => ({ value: a, label: a }))} /></label>
          <label style={genLabel}>Site of care
            <SearchableSelect value={site} onChange={setSite} ariaLabel="Site of care" style={{ minWidth: 120 }}
              options={[{ value: 'home', label: 'home' }, { value: 'aic', label: 'AIC / AIS' }]} /></label>
          {isIv && <label style={genLabel}>Pump (this visit)
            <SearchableSelect value={pump} onChange={setPump} ariaLabel="Pump" style={{ minWidth: 160 }}
              options={pumpOptions} /></label>}
          <label style={genLabel}>Pack level
            <SearchableSelect value={pack} onChange={setPack} ariaLabel="Pack level" style={{ minWidth: 140 }}
              options={['standard', 'conservative'].map(p => ({ value: p, label: p }))} /></label>
          <button onClick={generate} disabled={busy || !drug || !selectedDrug?.approved} style={primaryBtn}>
            {busy ? <Loader2 size={14} className="spin" /> : <PlayCircle size={14} />} Generate</button>
          <button onClick={runAi} disabled={aiBusy || !drug} style={aiBtn}>
            {aiBusy ? <Loader2 size={14} className="spin" /> : <Sparkles size={14} />} AI supply lookup</button>
        </div>
        {selectedDrug && !selectedDrug.approved && (
          <div style={{ fontSize: 12, color: '#92600a', marginTop: 8 }}>
            This drug's supply profile is not approved yet — run the AI lookup, review the citations, save to the drug profile, then approve it from the Drug Reference record.
          </div>
        )}
        {msg && <div style={{ fontSize: 12.5, color: '#b91c1c', marginTop: 8 }}>{msg}</div>}
      </div>

      {/* AI lookup result */}
      {(ai || aiMsg) && (
        <div style={{ ...card, borderColor: '#c9b8f5' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Sparkles size={15} /> AI supply lookup {ai?.model ? <code style={codeChip}>{ai.model}</code> : null}
              {ai?.search_count != null && <span style={{ ...muted, fontWeight: 400, fontSize: 11.5 }}>{ai.search_count} searches over official sources only</span>}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {ai?.status === 'ok' && <button style={btnSm} disabled={aiBusy} onClick={() => void rerunAi()}>Re-run</button>}
              {ai?.status === 'ok' && !ai.accepted_at && (
                <button style={{ ...primaryBtn, padding: '6px 11px', fontSize: 12.5 }} disabled={acceptBusy} onClick={() => void acceptAi()}>
                  {acceptBusy ? <Loader2 size={13} className="spin" /> : <Save size={13} />} Save to drug profile</button>
              )}
              {ai?.accepted_at && <span style={okBadge}><CheckCircle2 size={11} /> saved to drug profile</span>}
            </div>
          </div>
          {aiMsg && <div style={{ fontSize: 12, marginTop: 6, color: aiMsg.startsWith('saved') ? '#0a7a1d' : '#92600a' }}>{aiMsg}</div>}
          {ai?.fields && (
            <>{aiFx.chips}
            <table {...aiFx.tableProps} style={{ ...lineTable, marginTop: 10 }}>
              <thead><tr><th style={th}>Field {aiFx.funnel('field')}</th><th style={th}>Proposed value {aiFx.funnel('value')}</th><th style={th}>Reference {aiFx.funnel('reference')}</th></tr></thead>
              <tbody>
                {aiFx.rows.map(({ field, value, cites }) => {
                  return (
                    <tr key={field}>
                      <td style={{ ...td, fontWeight: 600 }}>{field}</td>
                      <td style={td}><code style={codeChip}>{aiValueText(value)}</code></td>
                      <td style={td}>
                        {cites.length === 0 && <span style={{ color: '#b91c1c' }}>no citation — will not save</span>}
                        {cites.map((c, i) => (
                          <div key={i} style={{ marginBottom: 3 }}>
                            <a href={c.url} target="_blank" rel="noreferrer" style={{ fontSize: 11.5 }}>{c.title ?? c.url}</a>
                            {' '}<span style={tierBadge(c.source_tier)}>{c.source_tier.replace('_', ' ')}</span>
                            {c.quoted_text && <div style={{ ...muted, fontSize: 11, fontStyle: 'italic' }}>&ldquo;{c.quoted_text}&rdquo;</div>}
                          </div>
                        ))}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table></>
          )}
          {ai?.dropped && ai.dropped.length > 0 && (
            <div style={{ fontSize: 11.5, color: '#92600a', marginTop: 8 }}>
              Dropped (no allow-listed citation): {ai.dropped.join('; ')}
            </div>
          )}
        </div>
      )}

      {/* Generated pick list */}
      {gen && (
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', alignItems: 'start' }}>
            <div>
              <div style={{ fontSize: 13.5, fontWeight: 700 }}>
                Pick list · {gen.drug_name} · {gen.access} · {gen.site_of_care === 'aic' ? 'AIC/AIS' : gen.site_of_care} · {gen.pack_level}
              </div>
              <div style={{ ...muted, fontSize: 11.5, marginTop: 2 }}>
                rules v{gen.rules_version} · {gen.count} items from catalog{gen.unmet.length > 0 ? ` · ${gen.unmet.length} profile requirements unmet` : ''}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {gen.state === 'complete'
                ? <span style={okBadge}><CheckCircle2 size={11} /> complete</span>
                : <span style={warnBadge}><AlertTriangle size={11} /> catalog incomplete</span>}
              <button style={ghostBtn} onClick={() => window.print()}><Printer size={13} /> Print checklist{gen.state !== 'complete' ? ' (INCOMPLETE)' : ''}</button>
            </div>
          </div>

          {/* Profile chips */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
            {gen.profile?.filter_min_micron != null && <span style={chip}>Filter: {gen.profile.filter_min_micron} to {gen.profile.filter_max_micron} micron (label)</span>}
            {gen.pump && <span style={chip}>Pump: {gen.pump}</span>}
            {gen.pump_set && <span style={chip}>Set: {gen.pump_set}{gen.priming_volume_ml != null ? ` · priming ${gen.priming_volume_ml} mL` : ''}</span>}
            {gen.profile?.diluent && <span style={chip}>Diluent: {gen.profile.diluent}{gen.profile.standard_final_volume_ml ? ` · ${gen.profile.standard_final_volume_ml} mL` : ''}{gen.profile.final_conc_max_mg_ml ? ` · ${gen.profile.final_conc_min_mg_ml ?? '?'}–${gen.profile.final_conc_max_mg_ml} mg/mL` : ''}</span>}
            {gen.profile?.co_infusion_allowed === false && <span style={chip}>No co-infusion — flush the line</span>}
            {gen.profile?.premedication_required === false && <span style={chip}>Premedication: none required</span>}
            {gen.concentration_note && <span style={{ ...chip, background: '#fef6e7', borderColor: '#f3d9a4' }}>{gen.concentration_note}</span>}
            {gen.profile?.rescue_override_reason && <span style={{ ...chip, background: '#fef6e7', borderColor: '#f3d9a4' }}>Rescue kit waived: {gen.profile.rescue_override_reason}</span>}
          </div>

          {pickFx.chips}
          <table {...pickFx.tableProps} style={{ ...lineTable, marginTop: 10 }}>
            <thead><tr><th style={th}>Category {pickFx.funnel('category')}</th><th style={th}>Item {pickFx.funnel('item')}</th><th style={th}>Vendor # {pickFx.funnel('vendor')}</th><th style={th}>Qty {pickFx.funnel('qty')}</th><th style={th}>Unit {pickFx.funnel('unit')}</th><th style={th}>Ext {pickFx.funnel('ext')}</th><th style={th}>Rule {pickFx.funnel('rule')}</th></tr></thead>
            <tbody>
              {pickFx.rows.map((i, ix) => (
                <tr key={ix}>
                  <td style={{ ...td, color: muted.color }}>{(i.category ?? '').replace(/_/g, ' ')}</td>
                  <td style={td}>{i.name}</td>
                  <td style={td}>{i.vendor_item_number ? <code style={codeChip}>{i.vendor_item_number}</code> : '—'}</td>
                  <td style={td}>{i.generated_qty}</td>
                  <td style={td}>{unitLabel(i)}</td>
                  <td style={td}>{money(i.ext_price)}</td>
                  <td style={{ ...td, color: muted.color, fontSize: 11.5 }}>{i.rule_note ?? i.rule_code}</td>
                </tr>
              ))}
              <tr>
                <td style={{ ...td, fontWeight: 700 }} colSpan={5}>Catalog supply cost, {gen.pack_level} pack</td>
                <td style={{ ...td, fontWeight: 700 }}>{money(gen.total_cost)}</td>
                <td style={td}></td>
              </tr>
            </tbody>
          </table>

          {gen.unmet.length > 0 && (
            <div style={{ background: '#fffbeb', border: '1px solid #f3d9a4', borderRadius: 6, padding: '9px 12px', marginTop: 10 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: '#92600a', display: 'flex', alignItems: 'center', gap: 6 }}>
                <AlertTriangle size={13} /> Required by profile, not in catalog · {gen.unmet.length} · list will not print as complete until these are added
              </div>
              <table style={{ ...lineTable, marginTop: 6 }}>
                <tbody>
                  {gen.unmet.map((u, ix) => (
                    <tr key={ix}>
                      <td style={td}>{u.item_name}</td>
                      <td style={td}>{u.qty}</td>
                      <td style={{ ...td, color: muted.color, fontSize: 11.5 }}>{u.rule_note ?? u.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {gen.excluded.length > 0 && (
            <details style={{ marginTop: 10 }}>
              <summary style={{ fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>Excluded by rule · {gen.excluded.length}</summary>
              <table style={{ ...lineTable, marginTop: 6 }}>
                <tbody>
                  {gen.excluded.map((e, ix) => (
                    <tr key={ix}>
                      <td style={{ ...td, color: muted.color }}>{e.item_name}</td>
                      <td style={{ ...td, color: muted.color, fontSize: 11.5 }}>{e.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}

          {(gen.profile?.source_citation || gen.profile?.stability_window) && (
            <div style={{ ...muted, fontSize: 11, marginTop: 10, borderTop: '1px solid var(--color-border, #eee)', paddingTop: 6, display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
              <span>
                {gen.profile.source_citation ? `Source: ${gen.profile.source_citation}. ` : ''}
                {gen.profile.stability_window ? `Stability: ${gen.profile.stability_window}.` : ''}
              </span>
              <span>Picker ______ Checker ______</span>
            </div>
          )}
        </div>
      )}

      {/* Catalog by category */}
      <div>
        <div style={sectionHead}>Categories &amp; items</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 8, marginTop: 8 }}>
          {cat.map(c => (
            <div key={c.id} style={card}>
              <div style={{ fontWeight: 700, fontSize: 12.5, marginBottom: 6 }}>{c.name}</div>
              {(itemsByCat[c.code] || []).map(i => (
                <div key={i.id} style={{ fontSize: 12, padding: '3px 0', borderTop: '1px solid var(--color-border, #eee)', display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <span>
                    {i.name} {i.vendor_item_number
                      ? <code style={codeChip}>{i.vendor_item_number}</code>
                      : <span style={{ ...warnBadge, fontSize: 9.5 }}>needs vendor #</span>}
                  </span>
                  <span style={muted}>{unitLabel(i)}</span>
                </div>
              ))}
              {(itemsByCat[c.code] || []).length === 0 && <div style={{ ...muted, fontSize: 11.5 }}>no items</div>}
            </div>
          ))}
        </div>
      </div>

      {/* Pump sets */}
      <div>
        <div style={sectionHead}><Cog size={14} /> Pump sets</div>
        {pumpFx.chips}
        <table {...pumpFx.tableProps} style={{ ...lineTable, marginTop: 8 }}>
          <thead><tr><th style={th}>Name {pumpFx.funnel('name')}</th><th style={th}>Pump {pumpFx.funnel('pump')}</th><th style={th}>Filter micron {pumpFx.funnel('filter')}</th><th style={th}>Priming (mL) {pumpFx.funnel('priming')}</th><th style={th}>Vendor # {pumpFx.funnel('vendor')}</th></tr></thead>
          <tbody>
            {pumpFx.rows.map(p => (
              <tr key={p.id}><td style={td}>{p.name}</td><td style={td}>{p.pump}</td><td style={td}>{p.filter_micron ?? '—'}</td><td style={td}>{p.priming_volume_ml ?? '—'}</td><td style={td}>{p.vendor_item_number ?? '—'}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

const muted: CSSProperties = { color: 'var(--color-text-soft, #6b7280)' }
const cardTitle: CSSProperties = { fontSize: 15, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }
const sectionHead: CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, fontWeight: 700, paddingBottom: 6, borderBottom: '2px solid var(--color-border, #e5e7eb)' }
const card: CSSProperties = { background: 'var(--color-surface, #fff)', border: '1px solid var(--color-border, #e5e7eb)', borderRadius: 8, padding: '11px 13px' }
const lineTable: CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 12 }
const th: CSSProperties = { textAlign: 'left', padding: '4px 8px', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.4px', color: 'var(--color-text-soft, #6b7280)', borderBottom: '1px solid var(--color-border, #e5e7eb)' }
const td: CSSProperties = { padding: '4px 8px', borderBottom: '1px solid var(--color-border, #f1f5f9)', verticalAlign: 'top' }
const codeChip: CSSProperties = { fontSize: 10.5, background: 'var(--color-surface-soft, #f2f5f7)', borderRadius: 3, padding: '0 4px' }
const genLabel: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11.5, fontWeight: 600, color: 'var(--color-text-soft, #6b7280)' }
const primaryBtn: CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 13px', background: 'var(--color-primary, #0d9624)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }
const aiBtn: CSSProperties = { ...primaryBtn, background: '#6741d9' }
const ghostBtn: CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 11px', background: 'var(--color-surface, #fff)', color: 'inherit', border: '1px solid var(--color-border, #d1d5db)', borderRadius: 6, fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }
const btnSm: CSSProperties = { ...ghostBtn, padding: '3px 8px', fontSize: 11.5 }
const chip: CSSProperties = { fontSize: 11, background: 'var(--color-surface-soft, #eef4f8)', border: '1px solid var(--color-border, #d8e4ec)', borderRadius: 10, padding: '2px 9px' }
const okBadge: CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, fontWeight: 800, color: '#0a7a1d', background: '#e9f7ec', border: '1px solid #b9e3c3', borderRadius: 9, padding: '1px 7px' }
const warnBadge: CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, fontWeight: 800, color: '#92600a', background: '#fef6e7', border: '1px solid #f3d9a4', borderRadius: 9, padding: '1px 7px' }
const tierBadge = (tier: string): CSSProperties => ({
  fontSize: 9.5, fontWeight: 700, borderRadius: 8, padding: '0 6px', border: '1px solid',
  ...(tier === 'fda_label' ? { color: '#0a7a1d', background: '#e9f7ec', borderColor: '#b9e3c3' }
    : tier === 'vendor_catalog' ? { color: '#1d4ed8', background: '#eff6ff', borderColor: '#bfdbfe' }
    : tier === 'state_board' ? { color: '#92600a', background: '#fef6e7', borderColor: '#f3d9a4' }
    : { color: '#6b7280', background: '#f3f4f6', borderColor: '#e5e7eb' }),
})
